import React, { useEffect, useCallback, useRef, useMemo, memo } from "react";
import { Button, IconButton, Typography, makeStyles } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ClearIcon from "@material-ui/icons/Clear";
import SubdirectoryArrowLeftIcon from "@material-ui/icons/SubdirectoryArrowLeft";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  getAccountColor,
  getTrackCarDisplayFields,
} from "../../../../utils/trackListUtils";
import {
  getShipmentStatusFromTrackCar,
  getRailCarNumberFromTrackCar,
  haveRailcarDisplayFieldsChanged,
} from "../utils/trackLocationUtils";
import { UNSPOTTED_TRACK_ID } from "../../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  accountIndicator: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 1,
  },
  productIndicator: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    width: theme.spacing(1),
    zIndex: 3,
    pointerEvents: "none",
  },
  carName: {
    display: "flex",
    alignItems: "center",
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  accountName: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(3),
    marginTop: theme.spacing(-0.5),
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  dragging: {
    opacity: 0,
    pointerEvents: "none",
  },
  overlay: {
    cursor: "grabbing",
    zIndex: 999,
    scale: 1.02,
    boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
  },
  grab: {
    cursor: "grab",
  },
}));

/**
 * Railcar item component with @dnd-kit support
 * Handles drag-drop, long-press, click, and multi-select interactions
 *
 * Props are grouped for better organization:
 * - handlers: Action callbacks (openShipment, onShipmentClick, etc.)
 * - uiConfig: UI configuration (isXSmall, getShipmentClassName, etc.)
 * - selectionState: Selection and filter state (isSelected, canDelete, etc.)
 */
export const RailcarItem = memo(
  ({
    shipment,
    index,
    trackLocationId,
    isOverlay = false,
    isDraggingGlobal = false,
    // Grouped props (required)
    handlers,
    uiConfig,
    selectionState,
  }) => {
    const {
      openShipment,
      onShipmentClick,
      onShipmentLongPress,
      handleDeleteConfirm,
      removeFromTrack,
      addItem,
      onMouseEnter,
      onMouseLeave,
    } = handlers;

    const {
      getShipmentClassName,
      getShipmentIcon,
      isXSmall,
      showPlaceAction,
      colorsLoaded,
    } = uiConfig;

    const { canDelete, canAdd, canOpenShipment } = selectionState;
    const classes = useStyles();
    const {
      accountId,
      accountName,
      railCarNumber,
      productName,
      productColor: trackCarProductColor,
    } = getTrackCarDisplayFields(shipment);
    // Priority: 1. Global Color Map (Source of Truth), 2. Normalized Shipment Field, 3. Null
    const accountColor = useMemo(() => {
      // colorsLoaded dependency ensures we recalculate when global color map updates
      if (colorsLoaded < 0) return null;
      const globalColor = accountId ? getAccountColor(accountId) : null;
      return globalColor || null;
    }, [accountId, colorsLoaded]);

    const productColor = useMemo(() => {
      if (colorsLoaded < 0) return null;
      return trackCarProductColor || null;
    }, [trackCarProductColor, colorsLoaded]);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: shipment.id,
      data: {
        type: "railcar",
        shipment,
        index,
      },
    });

    // Merge dnd-kit pointerDown with custom logic
    // IMPORTANT: We now keep listeners on the outer div for standard dnd-kit behavior
    // but we still need pointer handlers on the inner div for long-press selection logic.

    // Track Long Press State
    const longPressTimeoutRef = useRef(null);
    const isLongPressRef = useRef(false);
    const startCoordRef = useRef({ x: 0, y: 0 });

    // Clear timer helper
    const clearLongPressTimer = useCallback(() => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
      }
    }, []);

    // Cleanup timer on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
          longPressTimeoutRef.current = null;
        }
      };
    }, []);

    // 1. Handle Pointer Down (Start Timer & Pass to dnd-kit)
    // CRITICAL: Don't prevent default or stop propagation - let click event fire naturally
    const handlePointerDown = useCallback(
      (e) => {
        isLongPressRef.current = false;
        startCoordRef.current = { x: e.clientX, y: e.clientY };
        clearLongPressTimer();

        // Start Long Press Timer (200ms for responsive multi-select)
        longPressTimeoutRef.current = setTimeout(() => {
          isLongPressRef.current = true;
          if (onShipmentLongPress) {
            onShipmentLongPress(shipment, trackLocationId);
          }
        }, 200);

        // CRITICAL: We let dnd-kit handle its own pointer down on the outer div
        // while we use this inner handler for long-press selection logic.
      },
      [clearLongPressTimer, onShipmentLongPress, shipment, trackLocationId],
    );

    // 2. Cancel Long Press on Move (but NOT on Up - let long press complete if timer fired)
    const handlePointerMove = useCallback(
      (e) => {
        // Only cancel if moved significantly (drag detected)
        const moveX = Math.abs(e.clientX - (startCoordRef.current.x || 0));
        const moveY = Math.abs(e.clientY - (startCoordRef.current.y || 0));
        if (moveX > 10 || moveY > 10) {
          clearLongPressTimer();
        }
      },
      [clearLongPressTimer],
    );

    // 2b. Handle Pointer Up - Clear timer if it hasn't fired yet
    const handlePointerUp = useCallback(() => {
      // Only clear timer if long press hasn't fired yet
      // If it already fired, keep the flag so click handler can see it
      if (!isLongPressRef.current) {
        clearLongPressTimer();
      }
      // Note: If long press already fired, isLongPressRef.current is true
      // and we keep it that way so handleClick can detect it and prevent opening
    }, [clearLongPressTimer]);

    const isOpenable = useMemo(
      () => canOpenShipment(shipment),
      [canOpenShipment, shipment],
    );

    // 3. The Native Click Handler - SIMPLIFIED like RailcarCard
    const handleClick = useCallback(
      (e) => {
        // Clear any pending long press timer
        clearLongPressTimer();

        // If dnd-kit thinks we are dragging, do not open
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // If we triggered a long press, do not open (long press already handled selection)
        if (isLongPressRef.current) {
          e.preventDefault();
          e.stopPropagation();
          // Reset flag after handling - this allows next interaction to work
          isLongPressRef.current = false;
          return;
        }

        // Allow selection (single and multi) even when shipment can't be opened.
        // Handler opens (if openable + simple click) or selects (if !openable or multi-select).
        if (onShipmentClick) {
          onShipmentClick(e, shipment, trackLocationId);
        } else {
          openShipment(shipment);
        }
      },
      [
        isDragging,
        onShipmentClick,
        openShipment,
        shipment,
        trackLocationId,
        clearLongPressTimer,
      ],
    );

    const style = useMemo(
      () => ({
        transform: CSS.Translate.toString(transform),
        transition: isDragging ? "none" : transition,
        // CRITICAL: 'touch-action: none' tells the browser "Don't scroll, let JS handle touches"
        touchAction: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        "--account-color": accountColor || "transparent",
        "--product-color": productColor || "transparent",
        backfaceVisibility: "hidden",
      }),
      [transform, transition, isDragging, accountColor, productColor],
    );

    const containerClassName = useMemo(() => {
      const baseClass = getShipmentClassName(shipment);
      const grabClass = isOverlay ? classes.overlay : classes.grab;
      const dragClass = isDragging ? classes.dragging : "";
      return `${baseClass} ${grabClass} ${dragClass}`;
    }, [
      shipment,
      isDragging,
      isOverlay,
      getShipmentClassName,
      classes,
    ]);

    // Stop Propagation Helper for inner buttons
    // This prevents the Row's "Tap Detection" from firing when you click "Delete"
    const stopProp = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const isUnspottedTrack = trackLocationId === UNSPOTTED_TRACK_ID;

    const handleMouseEnterLocal = useCallback(
      (e) => {
        if (!onMouseEnter) return;
        const carNum = railCarNumber || "Unknown Car";
        const accName = accountName || "Unknown Account";
        const prodName = productName ? ` - ${productName}` : "";
        const title = `${carNum} | ${accName}${prodName}`;
        onMouseEnter(e, title);
      },
      [onMouseEnter, railCarNumber, accountName, productName],
    );

    const Content = (
      <div
        className={containerClassName}
        onClick={handleClick}
        onMouseEnter={handleMouseEnterLocal}
        onMouseLeave={onMouseLeave}
        style={!isOpenable ? { cursor: "pointer" } : undefined}
        // Pointer handlers for long press - on the content div to avoid competing with dnd-kit outer listeners
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={clearLongPressTimer}
        onPointerMove={handlePointerMove}
      >
        {accountColor && (
          <div
            className={classes.accountIndicator}
            style={{ backgroundColor: "var(--account-color)" }}
          />
        )}
        {productColor && (
          <div
            className={classes.productIndicator}
            style={{ backgroundColor: "var(--product-color)" }}
          />
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
            position: "relative",
            zIndex: 2,
            minWidth: 0,
          }}
        >
          <Typography variant="body2" className={classes.carName}>
            {getShipmentIcon(shipment)}
            {`${index + 1}.`} {`${railCarNumber}`}
          </Typography>
          {accountName && (
            <Typography variant="caption" className={classes.accountName}>
              {accountName}
            </Typography>
          )}
        </div>
        {!isOverlay && ( // Hide controls while dragging
          <span
            className={classes.actionIcons}
            // Stop pointer events here so clicking buttons doesn't trigger row drag or long press
            onPointerDown={stopProp}
            onPointerUp={stopProp}
            // Also stop clicks bubbling up to the row
            onClick={stopProp}
          >
            {(typeof canDelete === "function"
              ? canDelete(shipment)
              : canDelete) && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteConfirm(shipment, trackLocationId);
                }}
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
            {!isUnspottedTrack && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromTrack(index, trackLocationId, shipment.id);
                }}
              >
                <ClearIcon />
              </IconButton>
            )}
            {showPlaceAction &&
              (isXSmall ? (
                <IconButton
                  disabled={!canAdd}
                  onClick={(e) => {
                    e.stopPropagation();
                    addItem(index);
                  }}
                >
                  <SubdirectoryArrowLeftIcon />
                </IconButton>
              ) : (
                <Button
                  disabled={!canAdd}
                  startIcon={<SubdirectoryArrowLeftIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    addItem(index);
                  }}
                >
                  Place
                </Button>
              ))}
          </span>
        )}
      </div>
    );

    if (isOverlay) {
      // Use a bare container for the preview so we don't inherit track-level padding/size
      return <div style={style}>{Content}</div>;
    }

    return (
      <div
        id={`railcar-${shipment.id}`}
        ref={setNodeRef}
        style={style}
        onContextMenu={(e) => e.preventDefault()}
        {...attributes}
        {...listeners}
      >
        {Content}
      </div>
    );
  },
  (prevProps, nextProps) => {
    const prevCar = prevProps.shipment;
    const nextCar = nextProps.shipment;
    const prevStatus = getShipmentStatusFromTrackCar(prevCar);
    const nextStatus = getShipmentStatusFromTrackCar(nextCar);
    if (
      prevCar.id !== nextCar.id ||
      prevStatus.status !== nextStatus.status ||
      prevStatus.state !== nextStatus.state ||
      getRailCarNumberFromTrackCar(prevCar) !== getRailCarNumberFromTrackCar(nextCar) ||
      haveRailcarDisplayFieldsChanged(prevCar, nextCar) ||
      prevProps.index !== nextProps.index ||
      prevProps.trackLocationId !== nextProps.trackLocationId ||
      prevProps.isOverlay !== nextProps.isOverlay ||
      prevProps.isDraggingGlobal !== nextProps.isDraggingGlobal
    ) {
      return false;
    }

    const prevSelection = prevProps.selectionState;
    const nextSelection = nextProps.selectionState;
    if (
      prevSelection.canDelete !== nextSelection.canDelete ||
      prevSelection.canAdd !== nextSelection.canAdd ||
      prevSelection.isSelected !== nextSelection.isSelected ||
      prevSelection.accountFilterId !== nextSelection.accountFilterId ||
      prevSelection.canOpenShipment !== nextSelection.canOpenShipment
    ) {
      return false;
    }

    if (prevProps.uiConfig !== nextProps.uiConfig) {
      return false;
    }

    if (prevProps.handlers !== nextProps.handlers) {
      return false;
    }

    return true;
  },
);
